import { ToastLevel } from '@src/common/components/ToastLevel';
import { useToast } from '@src/common/hooks/useToast';
import { FormFileModes } from '@src/documents/components/FormFileModes';
import {
  FormFileUpload,
  type WithDocuments,
} from '@src/documents/components/FormFileUpload';
import { upsertFormDocument } from '@src/documents/components/UpsertFormDocument';
import { useDeleteDocument } from '@src/documents/hooks/useDeleteDocument';
import { useMarkToSendDocument } from '@src/documents/hooks/useMarkToSendDocument';
import { useShowDocumentToClient } from '@src/documents/hooks/useShowDocumentToClient';
import { useUploadDocument } from '@src/documents/hooks/useUploadDocument';
import type { FileWithPath } from 'react-dropzone';
import type { Control, FieldValues } from 'react-hook-form';
import { useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface Props {
  permitApplicationId?: string;
  orderId: string;
  control: Control<FieldValues>;
  mode: FormFileModes;
  onSuccessfulUpload?: () => void;
  readOnly?: boolean;
  isSmall?: boolean;
}

export const DocumentFormFileUpload = ({
  permitApplicationId,
  control,
  readOnly,
  onSuccessfulUpload,
  mode,
  orderId,
  isSmall,
}: Props) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const fieldArray = useFieldArray({
    control: control as Control<WithDocuments>,
    name: 'documents',
  });

  const { uploadDocument } = useUploadDocument({
    permitId: permitApplicationId,
    orderId,
    sendToClient: mode === FormFileModes.SENT_TO_CLIENT,
    onSuccess: (newDocument) => {
      upsertFormDocument(newDocument, fieldArray);
      if (onSuccessfulUpload) {
        onSuccessfulUpload();
      }
      showToast({
        severity: ToastLevel.success,
        message: t('fileUpload.success'),
      });
    },
    onError: () => {
      showToast({
        severity: ToastLevel.error,
        message: t('fileUpload.error'),
      });
    },
  });
  const { deleteDocument } = useDeleteDocument({
    onSuccess: ({ index }) => {
      fieldArray.remove(index);
      showToast({
        severity: ToastLevel.success,
        message: t('fileDeletion.success'),
      });
    },
    onError: () => {
      showToast({
        severity: ToastLevel.error,
        message: t('fileDeletion.error'),
      });
    },
  });

  const onDrop = (acceptedFiles: FileWithPath[]) => {
    acceptedFiles.forEach((file: FileWithPath) => {
      uploadDocument(file);
    });
  };

  const onDelete = ({ id, index }: { id?: string; index: number }) => {
    if (id) {
      deleteDocument({ documentId: id, index });
    }
  };
  const documentFound = (documentToChangeName: number) => {
    return documentToChangeName !== -1;
  };
  const onRename = ({ id, name }: { id?: string; name?: string }) => {
    if (id && name) {
      const documentToChangeName = fieldArray.fields.findIndex(
        (field) => field.documentId === id,
      );
      if (documentFound(documentToChangeName)) {
        const updatedField = {
          ...fieldArray.fields[documentToChangeName],
          displayName: name,
        };
        fieldArray.update(documentToChangeName, updatedField);
      }
    }
  };

  const { markToShowToClient } = useShowDocumentToClient({ orderId });
  const { markToSend } = useMarkToSendDocument({ orderId });

  const onCheck = ({ id, checked }: { id?: string; checked: boolean }) => {
    if (id && mode === FormFileModes.SENT_TO_CLIENT) {
      markToSend({
        documentId: id,
        sendToClient: checked,
      });
    }
    if (id && mode === FormFileModes.MAKE_VISIBLE_TO_CLIENT) {
      markToShowToClient({
        documentId: id,
        showToClient: checked,
      });
    }
  };
  return (
    <FormFileUpload
      isSmall={isSmall}
      control={control as Control<WithDocuments>}
      onDrop={onDrop}
      onDelete={onDelete}
      onCheck={onCheck}
      onRename={onRename}
      fields={fieldArray.fields}
      readOnly={readOnly}
      mode={mode}
    />
  );
};
